import { useState, useMemo } from 'react';

export type SortDir = 'asc' | 'desc' | null;

export interface SortState {
  key: string | null;
  dir: SortDir;
}

/** Returns sorted rows + sort state + a toggle handler. */
export function useSort<T extends Record<string, unknown>>(rows: T[]) {
  const [sort, setSort] = useState<SortState>({ key: null, dir: null });

  const toggleSort = (key: string) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc')  return { key, dir: 'desc' };
      return { key: null, dir: null }; // third click clears sort
    });
  };

  const sorted = useMemo(() => {
    if (!sort.key || !sort.dir) return rows;
    const k = sort.key;
    const mul = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[k] ?? '';
      const bv = b[k] ?? '';
      // Numeric compare if both look numeric
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * mul;
      // Date strings
      const ad = Date.parse(String(av));
      const bd = Date.parse(String(bv));
      if (!isNaN(ad) && !isNaN(bd)) return (ad - bd) * mul;
      // String
      return String(av).localeCompare(String(bv)) * mul;
    });
  }, [rows, sort]);

  return { sorted, sort, toggleSort };
}
